import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import {
  Lock,
  MessageCircleQuestion,
  BadgeCheck,
  Users,
  Eye,
  Sparkles,
  Check,
  ArrowRight,
  ChevronDown,
} from "lucide-react";
import { LANGUAGES, translations } from "../i18n/translations";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LOGO =
  "https://customer-assets.emergentagent.com/job_focus-love-app/artifacts/wqa1c5lx_reveal%20logo.png";

const fadeUp = {
  hidden: { opacity: 0, y: 28 },
  show: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.7, delay: i * 0.08, ease: [0.22, 1, 0.36, 1] },
  }),
};

const Reveal = ({ children, className = "", i = 0 }) => (
  <motion.div
    className={className}
    variants={fadeUp}
    custom={i}
    initial="hidden"
    whileInView="show"
    viewport={{ once: true, amount: 0.25 }}
  >
    {children}
  </motion.div>
);

const Label = ({ children, ...props }) => (
  <span
    className="block text-[0.7rem] sm:text-xs font-body font-medium uppercase tracking-[0.32em] text-reveal-rose mb-5"
    {...props}
  >
    {children}
  </span>
);

const stepNumbers = ["01", "02", "03"];
const featureIcons = [Lock, MessageCircleQuestion, BadgeCheck, Users, Eye, Sparkles];
const planMeta = [
  { key: "free", price: "€0", period: false, popular: false },
  { key: "connect", price: "€9.99", period: true, popular: true },
  { key: "premium", price: "€24.99", period: true, popular: false },
];
const planNames = { connect: "Connect", premium: "Premium" };
const referralValues = ["Instagram", "TikTok", "Twitter/X", "Friend", "Google", "Other"];

const SUPPORTED = LANGUAGES.map((l) => l.code);

const detectLanguage = () => {
  try {
    const stored = localStorage.getItem("reveal_lang");
    if (stored && SUPPORTED.includes(stored)) return stored;
    const nav = (navigator.language || "en").split("-")[0];
    return SUPPORTED.includes(nav) ? nav : "en";
  } catch {
    return "en";
  }
};

const detectCountry = () => {
  try {
    const loc = new Intl.Locale(navigator.language);
    return loc.region || null;
  } catch {
    return null;
  }
};

const LanguageSwitcher = ({ lang, setLang }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const current = LANGUAGES.find((l) => l.code === lang) || LANGUAGES[0];

  useEffect(() => {
    const onClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  return (
    <div ref={ref} className="relative" data-testid="language-switcher">
      <button
        onClick={() => setOpen((o) => !o)}
        data-testid="language-switcher-button"
        className="inline-flex items-center gap-2 rounded-full border border-reveal-rose/30 bg-black/40 backdrop-blur px-4 py-2 text-sm text-reveal-cream hover:border-reveal-rose/60 transition-colors"
      >
        <span className="text-base leading-none">{current.flag}</span>
        <span className="font-body hidden sm:inline">{current.name}</span>
        <ChevronDown className={`w-4 h-4 text-reveal-rose transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div
          className="absolute right-0 mt-2 w-44 rounded-2xl border border-reveal-rose/20 bg-reveal-burgundy py-2 shadow-[0_20px_60px_-20px_rgba(0,0,0,0.85)] z-50"
          data-testid="language-switcher-menu"
        >
          {LANGUAGES.map((l) => (
            <button
              key={l.code}
              onClick={() => {
                setLang(l.code);
                try { localStorage.setItem("reveal_lang", l.code); } catch { /* ignore */ }
                setOpen(false);
              }}
              data-testid={`language-option-${l.code}`}
              className={`w-full flex items-center gap-3 px-4 py-2 text-sm text-left transition-colors hover:bg-reveal-rose/10 ${
                l.code === lang ? "text-reveal-rose" : "text-reveal-cream/80"
              }`}
            >
              <span className="text-base leading-none">{l.flag}</span>
              <span className="font-body">{l.name}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

const WaitlistForm = ({ testidPrefix, onJoined, t, lang }) => {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [referral, setReferral] = useState("");
  const [loading, setLoading] = useState(false);

  const referralOptions = referralValues.map((v) => ({
    value: v,
    label: v === "Friend" ? t.referral.friend : v === "Other" ? t.referral.other : v,
  }));

  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setLoading(true);
    try {
      await axios.post(`${API}/waitlist`, {
        email: email.trim(),
        name: name.trim() || null,
        referral: referral || null,
        language: lang,
        country: detectCountry(),
        source: "landing",
      });
      toast.success(t.form.success, { description: t.form.successDesc });
      setName("");
      setEmail("");
      setReferral("");
      onJoined?.();
    } catch (err) {
      let msg;
      if (err?.response?.status === 409) {
        msg = t.form.duplicate;
      } else if (!err?.response) {
        // No response = network/CORS/server-unreachable
        msg = t.form.network;
      } else {
        msg = err?.response?.data?.detail || t.form.generic;
        if (typeof msg !== "string") msg = t.form.generic;
      }
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const fieldClass =
    "w-full bg-black/40 border border-reveal-rose/50 rounded-2xl px-6 py-3.5 font-body text-reveal-cream placeholder:text-reveal-cream/40 outline-none focus:border-reveal-rose transition-colors";

  return (
    <form
      onSubmit={submit}
      className="flex flex-col gap-3 w-full max-w-xl mx-auto"
      data-testid={`${testidPrefix}-waitlist-form`}
    >
      <input
        type="text"
        value={name}
        onChange={(e) => setName(e.target.value)}
        placeholder={t.form.name}
        data-testid={`${testidPrefix}-waitlist-name-input`}
        className={fieldClass}
      />
      <input
        type="email"
        required
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        placeholder={t.form.email}
        data-testid={`${testidPrefix}-waitlist-email-input`}
        className={fieldClass}
      />
      <select
        value={referral}
        onChange={(e) => setReferral(e.target.value)}
        data-testid={`${testidPrefix}-waitlist-referral-select`}
        className={`${fieldClass} appearance-none cursor-pointer ${referral ? "text-reveal-cream" : "text-reveal-cream/40"}`}
      >
        <option value="" className="text-black">{t.form.referral}</option>
        {referralOptions.map((o) => (
          <option key={o.value} value={o.value} className="text-black">{o.label}</option>
        ))}
      </select>
      <button
        type="submit"
        disabled={loading}
        data-testid={`${testidPrefix}-waitlist-submit-button`}
        className="group inline-flex items-center justify-center gap-2 bg-reveal-rose text-black font-body font-medium px-8 py-3.5 rounded-full hover:bg-[#c9987b] transition-colors disabled:opacity-60 whitespace-nowrap"
      >
        {loading ? t.form.joining : t.form.submit}
        {!loading && (
          <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
        )}
      </button>
    </form>
  );
};

export default function Landing() {
  const [waitlistCount, setWaitlistCount] = useState(null);
  const [lang, setLang] = useState(detectLanguage());
  const t = translations[lang] || translations.en;

  const fetchCount = async () => {
    try {
      const res = await axios.get(`${API}/waitlist/count`);
      setWaitlistCount(res.data.count);
    } catch (e) {
      /* silent */
    }
  };

  useEffect(() => {
    fetchCount();
  }, []);

  const scrollToWaitlist = () => {
    document.getElementById("waitlist")?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <div
      className="min-h-screen bg-reveal-black text-reveal-cream font-body overflow-x-hidden"
      data-testid="landing-page"
    >
      {/* Language switcher */}
      <div className="fixed top-4 right-4 z-50" data-testid="language-switcher-wrap">
        <LanguageSwitcher lang={lang} setLang={setLang} />
      </div>

      {/* Section 1 — Hero */}
      <section className="relative px-5 pt-16 pb-24 sm:pt-20 sm:pb-28">
        <div
          className="pointer-events-none absolute inset-x-0 top-0 h-[520px] opacity-60"
          style={{
            background:
              "radial-gradient(ellipse 60% 100% at 50% 0%, rgba(183,134,106,0.16), transparent 70%)",
          }}
        />
        <div className="relative max-w-3xl mx-auto text-center">
          <Reveal>
            <img
              src={LOGO}
              alt="Reveal"
              data-testid="hero-logo"
              className="w-24 sm:w-28 h-auto mx-auto mb-10 select-none"
            />
          </Reveal>
          <Reveal i={1}>
            <Label>{t.hero.label}</Label>
          </Reveal>
          <Reveal i={2}>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl leading-[1.05] text-reveal-cream mb-6">
              {t.hero.headline}
            </h1>
          </Reveal>
          <Reveal i={3}>
            <p className="font-body text-base sm:text-lg text-reveal-cream/70 max-w-xl mx-auto leading-relaxed mb-9">
              {t.hero.sub}
            </p>
          </Reveal>
          <Reveal i={4}>
            <button
              onClick={scrollToWaitlist}
              data-testid="hero-join-waitlist-button"
              className="group inline-flex items-center gap-2 bg-reveal-rose text-black font-body font-medium px-9 py-3.5 rounded-full hover:bg-[#c9987b] transition-colors"
            >
              {t.hero.cta}
              <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
            </button>
            <p className="mt-4 font-body text-sm text-reveal-cream/55" data-testid="coming-soon-message">
              {t.landingCta.comingSoon}
            </p>
          </Reveal>
          <Reveal i={5}>
            <p className="mt-8 font-body text-sm text-reveal-cream/50 tracking-wide">
              {t.hero.pills.join("\u00A0\u00A0•\u00A0\u00A0")}
            </p>
          </Reveal>
        </div>
      </section>

      {/* Section 2 — The Problem */}
      <section className="px-5 pb-24">
        <Reveal className="max-w-4xl mx-auto">
          <div className="rounded-3xl bg-reveal-burgundy border border-reveal-rose/10 px-7 py-14 sm:px-16 sm:py-20 text-center">
            <Label>{t.problem.label}</Label>
            <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl text-reveal-cream mb-6 leading-tight">
              {t.problem.headline}
            </h2>
            <p className="font-body text-base text-reveal-cream/70 max-w-2xl mx-auto leading-relaxed">
              {t.problem.body}
            </p>
          </div>
        </Reveal>
      </section>

      {/* Section 3 — How It Works */}
      <section className="px-5 pb-24">
        <div className="max-w-5xl mx-auto text-center">
          <Reveal>
            <Label>{t.how.label}</Label>
            <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl text-reveal-cream mb-5">
              {t.how.headline}
            </h2>
            <p className="font-body text-base text-reveal-cream/70 max-w-2xl mx-auto leading-relaxed mb-14">
              {t.how.sub}
            </p>
          </Reveal>
          <div className="grid md:grid-cols-3 gap-5 text-left">
            {stepNumbers.map((n, idx) => {
              const s = t.how.steps[idx];
              return (
                <Reveal key={n} i={idx}>
                  <div
                    className={`h-full rounded-2xl border px-7 py-8 ${
                      idx === 1
                        ? "bg-reveal-burgundy border-reveal-rose/25"
                        : "bg-white/[0.02] border-reveal-rose/10"
                    }`}
                    data-testid={`how-step-${n}`}
                  >
                    <span className="font-heading text-4xl text-reveal-rose">{n}</span>
                    <h3 className="font-heading text-2xl text-reveal-cream mt-4 mb-2">{s.title}</h3>
                    <p className="font-body text-sm text-reveal-cream/65 leading-relaxed">
                      {s.desc}
                    </p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* Section 4 — Features */}
      <section className="px-5 pb-24">
        <div className="max-w-5xl mx-auto text-center">
          <Reveal>
            <Label>{t.features.label}</Label>
            <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl text-reveal-cream mb-5">
              {t.features.headline}
            </h2>
            <p className="font-body text-base text-reveal-cream/70 max-w-2xl mx-auto leading-relaxed mb-14">
              {t.features.sub}
            </p>
          </Reveal>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 text-left">
            {t.features.items.map((f, idx) => {
              const Icon = featureIcons[idx];
              return (
                <Reveal key={idx} i={idx % 3}>
                  <div
                    className="group h-full rounded-2xl bg-reveal-burgundy/70 border border-reveal-rose/10 px-7 py-8 hover:border-reveal-rose/35 transition-colors"
                    data-testid={`feature-card-${idx}`}
                  >
                    <div className="w-11 h-11 rounded-full bg-reveal-rose/10 flex items-center justify-center mb-5 group-hover:bg-reveal-rose/20 transition-colors">
                      <Icon className="w-5 h-5 text-reveal-rose" />
                    </div>
                    <h3 className="font-heading text-xl text-reveal-cream mb-2 leading-snug">
                      {f.title}
                    </h3>
                    <p className="font-body text-sm text-reveal-cream/65 leading-relaxed">
                      {f.desc}
                    </p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* Section 5 — Pricing */}
      <section className="px-5 pb-24">
        <div className="max-w-5xl mx-auto text-center">
          <Reveal>
            <Label>{t.pricing.label}</Label>
            <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl text-reveal-cream mb-5">
              {t.pricing.headline}
            </h2>
            <p className="font-body text-base text-reveal-cream/70 max-w-2xl mx-auto leading-relaxed mb-14">
              {t.pricing.sub}
            </p>
          </Reveal>
          <div className="grid md:grid-cols-3 gap-5 text-left items-stretch">
            {planMeta.map((p, idx) => {
              const displayName = p.key === "free" ? t.pricing.free : planNames[p.key];
              return (
                <Reveal key={p.key} i={idx} className="h-full">
                  <div
                    className={`relative h-full rounded-2xl border px-7 py-9 flex flex-col ${
                      p.popular
                        ? "bg-reveal-burgundy border-reveal-rose/40 shadow-[0_0_60px_-20px_rgba(183,134,106,0.5)]"
                        : "bg-white/[0.02] border-reveal-rose/10"
                    }`}
                    data-testid={`pricing-card-${p.key}`}
                  >
                    {p.popular && (
                      <span className="absolute -top-3 left-7 bg-reveal-rose text-black text-[0.62rem] font-body font-semibold uppercase tracking-[0.18em] px-3 py-1 rounded-full">
                        {t.pricing.popular}
                      </span>
                    )}
                    <h3 className="font-heading text-2xl text-reveal-cream mb-3">{displayName}</h3>
                    <div className="mb-7">
                      <span className="font-heading text-4xl text-reveal-cream">{p.price}</span>
                      <span className="font-body text-sm text-reveal-cream/55">
                        {p.period ? t.pricing.period : ""}
                      </span>
                    </div>
                    <ul className="space-y-3 mb-8 flex-1">
                      {t.pricing.plans[idx].features.map((feat) => (
                        <li key={feat} className="flex items-start gap-3">
                          <Check className="w-4 h-4 text-reveal-rose mt-0.5 shrink-0" />
                          <span className="font-body text-sm text-reveal-cream/75 leading-relaxed">
                            {feat}
                          </span>
                        </li>
                      ))}
                    </ul>
                    <button
                      onClick={scrollToWaitlist}
                      data-testid={`pricing-cta-${p.key}`}
                      className={`w-full rounded-full py-3 font-body font-medium transition-colors ${
                        p.popular
                          ? "bg-reveal-rose text-black hover:bg-[#c9987b]"
                          : "border border-reveal-rose/40 text-reveal-cream hover:bg-reveal-rose/10"
                      }`}
                    >
                      {t.pricing.join}
                    </button>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* Section 6 — Waitlist CTA */}
      <section id="waitlist" className="px-5 pb-24 scroll-mt-10">
        <Reveal className="max-w-4xl mx-auto">
          <div className="rounded-3xl bg-reveal-burgundy border border-reveal-rose/15 px-7 py-16 sm:px-16 sm:py-20 text-center">
            <Label>{t.cta.label}</Label>
            <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl text-reveal-cream mb-5">
              {t.cta.headline}
            </h2>
            <p className="font-body text-base text-reveal-cream/70 max-w-xl mx-auto leading-relaxed mb-9">
              {t.cta.body}
            </p>
            <WaitlistForm testidPrefix="cta" onJoined={fetchCount} t={t} lang={lang} />
            {waitlistCount !== null && waitlistCount > 0 && (
              <p
                className="mt-5 font-body text-sm text-reveal-rose"
                data-testid="waitlist-count"
              >
                {t.form.count.replace("{n}", waitlistCount.toLocaleString())}
              </p>
            )}
          </div>
        </Reveal>
      </section>

      {/* Footer */}
      <footer className="px-5 py-12 border-t border-reveal-rose/10">
        <div className="max-w-5xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-5">
          <span className="font-heading text-2xl text-reveal-rose" data-testid="footer-wordmark">
            Reveal
          </span>
          <div className="flex flex-wrap items-center justify-center gap-x-5 gap-y-2 font-body text-sm text-reveal-cream/55">
            <Link to="/terms" className="hover:text-reveal-cream transition-colors" data-testid="footer-terms-link">
              {t.footer.terms}
            </Link>
            <span className="text-reveal-cream/25">·</span>
            <Link to="/privacy" className="hover:text-reveal-cream transition-colors" data-testid="footer-privacy-link">
              {t.footer.privacy}
            </Link>
            <span className="text-reveal-cream/25">·</span>
            <Link to="/account-deletion" className="hover:text-reveal-cream transition-colors" data-testid="footer-account-deletion-link">
              {t.footer.deletion}
            </Link>
            <span className="text-reveal-cream/25">·</span>
            <Link to="/csae-policy" className="hover:text-reveal-cream transition-colors" data-testid="footer-csae-link">
              {t.footer.csae}
            </Link>
            <span className="text-reveal-cream/25">·</span>
            <Link to="/law-enforcement" className="hover:text-reveal-cream transition-colors" data-testid="footer-law-link">
              {t.footer.law}
            </Link>
            <span className="text-reveal-cream/25">·</span>
            <Link to="/acceptable-use" className="hover:text-reveal-cream transition-colors" data-testid="footer-aup-link">
              {t.footer.aup}
            </Link>
            <span className="text-reveal-cream/25">·</span>
            <a
              href="mailto:hello@getrevealapp.com"
              className="hover:text-reveal-cream transition-colors"
              data-testid="footer-email-link"
            >
              hello@getrevealapp.com
            </a>
          </div>
        </div>
        <p className="max-w-5xl mx-auto mt-8 font-body text-xs text-reveal-cream/35 text-center sm:text-left">
          {t.footer.rights}
        </p>
      </footer>
    </div>
  );
}
